import { type NormalizedTokenInfo, useTokenInfo } from '@/domain/euler'
import { useEulerIndexerELend } from '@/domain/euler/elends/elendsQuery'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { getEntities } from '@/features/evault-details/data/entities'
import { selectGovernorsFromAddresses } from '@/features/evault-details/logic/selectGovernors'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { Address, formatUnits } from 'viem'
import { useAccount, useReadContract, useReadContracts } from 'wagmi'
import { useMemo } from 'react'
import { getSubAccount } from '@/domain/evc'
import { decodeEulerCapToBigInt } from '@/domain/euler/shared/caps'

export interface ELendDetailsData {
  address: Address
  name: string
  symbol: string
  assetSymbol?: string
  underlyingTokenInfo?: NormalizedTokenInfo // Full token info from Euler indexer
  curatorName?: string
  market?: {
    key: string
    name: string
    description?: string
    entity: string | string[]
    url?: string
    isGovernanceLimited?: boolean
    isDeprecated?: boolean
  }
  decimals: number
  totalSupply: NormalizedUnitNumber
  totalBorrowed: NormalizedUnitNumber
  totalAssets: NormalizedUnitNumber
  availableLiquidity: NormalizedUnitNumber
  underlyingAsset: Address
  supplyAPY: number
  /** Lending APY from borrower interest (on-chain calculation) */
  lendingAPY: number
  /** Intrinsic APY from underlying asset (e.g., staking yield, PT yield) - from indexer */
  intrinsicAPY?: number
  borrowAPY: number
  utilization: number
  interestRate: bigint
  // Pricing from indexer
  assetPrice?: number // Asset price in USD from Euler indexer
  totalAssetsUSD?: number // Total assets in USD from Euler indexer
  // Risk parameters
  liquidationPenalty?: NormalizedUnitNumber
  supplyCap?: NormalizedUnitNumber
  borrowCap?: NormalizedUnitNumber
  supplyCapRaw?: bigint // Raw bigint value for calculations
  borrowCapRaw?: bigint // Raw bigint value for calculations
  totalAssetsRaw?: bigint // Raw bigint value for calculations
  totalBorrowedRaw?: bigint // Raw bigint value for calculations
  sharePrice?: NormalizedUnitNumber
  interestFee?: number
  // Addresses
  oracle?: Address
  unitOfAccount?: Address
  governor?: Address
  feeReceiver?: Address
  irmAddress?: Address
  hookTarget?: Address
  // User balances
  userBalance?: NormalizedUnitNumber
  userBorrows?: NormalizedUnitNumber
}

export interface UseELendDetailsResult {
  elend: ELendDetailsData | null
  isLoading: boolean
  error: Error | null
}

export function useELendDetails({
  vaultAddress,
  chainId,
}: { vaultAddress: Address; chainId: number }): UseELendDetailsResult {
  const { address: userAddress } = useAccount()

  // Get APY data from Euler indexer (includes intrinsic yield for escrow vaults)
  const indexerVault = useEulerIndexerELend(vaultAddress, chainId)

  const {
    data: vaultData,
    isLoading,
    error,
  } = useReadContracts({
    contracts: [
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'name',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'symbol',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'decimals',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'totalSupply',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'totalBorrows',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'totalAssets',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'asset',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'interestRate',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'oracle',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'unitOfAccount',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'governorAdmin',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'feeReceiver',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'interestRateModel',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'hookTarget',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'convertToAssets',
        args: [BigInt(1e18)],
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'caps',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'maxLiquidationDiscount',
        chainId,
      },
      {
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'interestFee',
        chainId,
      },
    ] as any,
    query: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
    },
  })

  // Get underlying asset address early for dependent queries
  const underlyingAssetAddress = vaultData?.[6]?.result as Address | undefined

  // Fetch underlying token info from Euler indexer (with onchain fallback)
  const { token: underlyingTokenInfo } = useTokenInfo(chainId, underlyingAssetAddress)

  // Get position (subaccount) address for user balance
  const position = useMemo(() => {
    if (!userAddress) return undefined
    return getSubAccount(userAddress, 0)
  }, [userAddress])

  // Fetch user balance from subaccount (where eLend deposits are stored)
  const { data: userBalanceData } = useReadContract({
    address: vaultAddress,
    abi: eVaultImplementationAbi,
    functionName: 'balanceOf',
    args: position ? [position] : undefined,
    chainId,
    query: {
      enabled: !!position,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
    },
  })

  // Only return null on initial load (when no data yet), not on refetches
  if (!vaultData) {
    return {
      elend: null,
      isLoading,
      error: error as Error | null,
    }
  }

  const name = vaultData[0]?.result as string | undefined
  const symbol = vaultData[1]?.result as string | undefined
  const decimals = vaultData[2]?.result as number | undefined
  const totalSupply = vaultData[3]?.result as bigint | undefined
  const totalBorrowed = vaultData[4]?.result as bigint | undefined
  const totalAssets = vaultData[5]?.result as bigint | undefined
  const underlyingAsset = vaultData[6]?.result as Address | undefined
  const interestRate = vaultData[7]?.result as bigint | undefined
  const oracle = vaultData[8]?.result as Address | undefined
  const unitOfAccount = vaultData[9]?.result as Address | undefined
  const governor = vaultData[10]?.result as Address | undefined
  const feeReceiver = vaultData[11]?.result as Address | undefined
  const irmAddress = vaultData[12]?.result as Address | undefined
  const hookTarget = vaultData[13]?.result as Address | undefined
  const sharePriceRaw = vaultData[14]?.result as bigint | undefined
  const capsData = vaultData[15]?.result as [number, number] | undefined // caps() returns [uint16 supplyCap, uint16 borrowCap]
  const maxLiquidationDiscountRaw = vaultData[16]?.result as number | undefined
  const interestFeeRaw = vaultData[17]?.result as number | undefined
  const userBalance = userBalanceData as bigint | undefined

  if (!name || !symbol || decimals === undefined || !underlyingAsset) {
    return {
      elend: null,
      isLoading: false,
      error: new Error('Failed to load vault data'),
    }
  }

  // Calculate APYs
  const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60
  const ratePerSecond = interestRate ? Number(formatUnits(interestRate, 27)) : 0
  const borrowAPY = interestRate ? ((1 + ratePerSecond) ** SECONDS_PER_YEAR - 1) * 100 : 0

  // Calculate utilization
  const utilization =
    totalAssets && totalBorrowed && totalAssets > 0n ? (Number(totalBorrowed) / Number(totalAssets)) * 100 : 0

  // Interest fee - convert from basis points (e.g., 1000 = 10%)
  const interestFeePercent = interestFeeRaw ? interestFeeRaw / 100 : 0

  // Calculate Supply APY - prefer indexer totalApy (includes intrinsic yield for escrow vaults)
  // Fall back to on-chain calculation if indexer data not available
  const onchainLendingAPY = borrowAPY * (utilization / 100) * (1 - interestFeePercent / 100)
  // Track whether we have valid totalApy from indexer
  const hasTotalApy = indexerVault?.totalApy != null
  // Intrinsic APY from indexer (e.g., staking yield, PT yield)
  // Only use intrinsic APY if we also have totalApy - otherwise decompounding would produce incorrect values
  const intrinsicAPY = hasTotalApy ? indexerVault?.intrinsicApy?.apy ?? undefined : undefined
  // Indexer totalApy is already in percentage format (e.g., 3.98 means 3.98%)
  const supplyAPY = hasTotalApy ? indexerVault!.totalApy! : onchainLendingAPY
  const lendingAPY =
    hasTotalApy && intrinsicAPY !== undefined && intrinsicAPY > 0
      ? supplyAPY - intrinsicAPY
      : supplyAPY

  // Calculate available liquidity
  const availableLiquidity = totalAssets !== undefined && totalBorrowed !== undefined ? totalAssets - totalBorrowed : 0n

  // Process caps - caps() returns [uint16, uint16] encoded values
  const supplyCapRaw = decodeEulerCapToBigInt(capsData?.[0])
  const borrowCapRaw = decodeEulerCapToBigInt(capsData?.[1])

  // Convert caps to normalized numbers for display
  const supplyCap = supplyCapRaw ? NormalizedUnitNumber(formatUnits(supplyCapRaw, decimals)) : undefined
  const borrowCap = borrowCapRaw ? NormalizedUnitNumber(formatUnits(borrowCapRaw, decimals)) : undefined

  // Liquidation penalty (max discount) - convert from basis points (e.g., 200 = 2%)
  const liquidationPenalty = maxLiquidationDiscountRaw
    ? NormalizedUnitNumber((maxLiquidationDiscountRaw / 100).toString())
    : undefined

  // Resolve curator name from governor address
  const entities = getEntities()
  const governorData = selectGovernorsFromAddresses(governor, entities)
  const curatorName = governorData.governors[0]?.name || ''

  // Get market info: indexer-first, hardcoded fallback
  const market = getVaultMarket(vaultAddress, indexerVault?.products)

  const elend: ELendDetailsData = {
    address: vaultAddress,
    name,
    symbol,
    assetSymbol: underlyingTokenInfo?.symbol,
    underlyingTokenInfo,
    curatorName,
    market,
    decimals,
    totalSupply:
      totalSupply !== undefined ? NormalizedUnitNumber(formatUnits(totalSupply, decimals)) : NormalizedUnitNumber('0'),
    totalBorrowed:
      totalBorrowed !== undefined
        ? NormalizedUnitNumber(formatUnits(totalBorrowed, decimals))
        : NormalizedUnitNumber('0'),
    totalAssets:
      totalAssets !== undefined ? NormalizedUnitNumber(formatUnits(totalAssets, decimals)) : NormalizedUnitNumber('0'),
    availableLiquidity: NormalizedUnitNumber(formatUnits(availableLiquidity, decimals)),
    underlyingAsset,
    supplyAPY,
    lendingAPY,
    intrinsicAPY,
    borrowAPY,
    utilization,
    interestRate: interestRate || 0n,
    // Pricing from indexer (most reliable source)
    assetPrice: indexerVault?.assetPrice ?? undefined,
    totalAssetsUSD: indexerVault?.totalAssetsUSD ?? undefined,
    liquidationPenalty,
    supplyCap,
    borrowCap,
    supplyCapRaw,
    borrowCapRaw,
    totalAssetsRaw: totalAssets,
    totalBorrowedRaw: totalBorrowed,
    sharePrice: sharePriceRaw ? NormalizedUnitNumber(formatUnits(sharePriceRaw, 18)) : undefined,
    interestFee: interestFeePercent,
    oracle,
    unitOfAccount,
    governor,
    feeReceiver,
    irmAddress,
    hookTarget,
    userBalance: userBalance !== undefined ? NormalizedUnitNumber(formatUnits(userBalance, decimals)) : undefined,
  }

  return {
    elend,
    isLoading: false,
    error: null,
  }
}
